// Reveal delivery patches one held row and its affected log group. Unread-book refresh
// remains a separate legacy duty until its publisher supplies an exact row tombstone.
import type { WSMessage } from './types';
import type { Roll } from '../log/types';

type Row = Record<string, unknown>;
type WSRollUpdateMessage = Extract<WSMessage, { type: 'roll_update' }> & { roll?: Row };
const held = new Map<string, Row>();
export const heldRollRows = (): Iterable<Row> => held.values();
function remember(row: Row): void {
    if (row.roll_i != null) held.set(String(row.roll_i), row);
    while (held.size > 200) held.delete(held.keys().next().value!);
}
if (typeof window !== 'undefined') {
    window.addEventListener?.('ft:log-rows-received', event => {
        held.clear();
        for (const row of (event as CustomEvent<{ rows: Row[] }>).detail.rows) remember(row);
    });
    document.addEventListener?.('ft-roll-authorized', event => {
        const row = (event as CustomEvent<{ roll: Row }>).detail.roll;
        if (row) remember(row);
    });
}

async function handleRollUpdate(msg: WSRollUpdateMessage): Promise<void> {
    if (msg.reason !== 'reveal') { await buildLog(); return; }
    const delta = msg.roll;
    if (!delta || delta.roll_i == null || !delta.roll_id) return;
    const encounterId = String(delta.encounter_id ?? '');
    const active = String(window.ftGetActiveEncounterId?.() ?? '');
    if (!window.FateTide?.user?.isGM && encounterId && active && encounterId !== active) return;
    const prior = held.get(String(delta.roll_i));
    if (prior && Number(prior.roll_private ?? 0) === Number(delta.roll_private ?? 0)
        && String(prior.roll_updated_by_id ?? '') === String(delta.roll_updated_by_id ?? '')) return;
    // Only this row is merged. A projected observer placeholder becomes the canonical saved row.
    const row = prior ? Object.assign(prior, delta) : delta;
    remember(row);
    const group = [...held.values()].filter(r => r.roll_id === row.roll_id).sort((a, b) => Number(b.roll_i) - Number(a.roll_i));
    const ul = document.querySelector<HTMLElement>('#div_sidelog_results > ul.list-group');
    if (!ul || typeof window.renderLogWindowFromJson !== 'function') return;
    const safe = String(row.roll_id).replace(/[^a-zA-Z0-9-]/g, '');
    const old = [...ul.querySelectorAll<HTMLElement>(`li[data-ft-roll-id="${safe}"]`)];
    const open = new Set(old.flatMap(node => [...node.querySelectorAll('.collapse.show')].map(el => el.id)));
    const built = window.renderLogWindowFromJson({ rolls: group as unknown as Roll[], campaign_permission: window.FateTide?.user?.isGM ? 1 : 0 });
    if (!built) return;
    const template = document.createElement('template');
    template.innerHTML = built.html;
    const fresh = [...template.content.querySelector('ul')?.children ?? []];
    for (const id of open) template.content.querySelector(`[id="${id.replace(/[^a-zA-Z0-9_-]/g, '')}"]`)?.classList.add('show');
    if (old.length) {
        for (const node of old) window.disposeBootstrapTooltipsWithin?.(node);
        old[0]!.replaceWith(...fresh);
        for (const node of old.slice(1)) node.remove();
    } else {
        // A formerly secret row had no node: insert in roll order, without refetching the window.
        const next = [...ul.children].find(node => {
            const rid = (node as HTMLElement).dataset.ftRollId;
            return group.length && [...held.values()].some(r => r.roll_id === rid && Number(r.roll_i) < Number(row.roll_i));
        });
        if (next) next.before(...fresh); else ul.append(...fresh);
    }
    for (const node of fresh) {
        if (node instanceof HTMLElement) {
            node.dataset.ftLiveGroup = String(row.roll_id);
            window.ftMountRuneIcons?.(node);
            if (typeof bootstrap !== 'undefined' && bootstrap.Tooltip) node.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => new bootstrap.Tooltip(el));
        }
    }
    window.activateLogCollapseTasks?.(built.collapseLabelTasks ?? []);
    window.dispatchEvent(new CustomEvent('ft:roll-row-updated', { detail: { row, replaced: old.length, inserted: fresh.length } }));
}
export { handleRollUpdate };
